import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../auth/useAuth';
import { useAccount } from '../onboarding/useAccount';
import { AppearanceCard } from './AppearanceCard';
import { BrandsCard } from './BrandsCard';
import { BrandSettingsForm } from './BrandSettingsForm';
import { DangerZone } from './DangerZone';
import { InstallCard } from './InstallCard';
import { NewsAutopostCard } from './NewsAutopostCard';
import { NotificationsCard } from './NotificationsCard';
import { PasswordSettingsForm } from './PasswordSettingsForm';
import { PlanCard } from './PlanCard';
import { TeamCard } from './TeamCard';

/**
 * Account settings, in two columns: the active brand's brief on the left,
 * the account itself on the right (its plan, every brand with the switch and add, the
 * sign-in details, this device's theme, and the two irreversible deletes). Brand fields stay in
 * the knowledge base and are linked from the brand form rather than
 * duplicated.
 */
export function SettingsPage() {
  const { business, businesses, error } = useAccount();
  const { user } = useAuth();

  return (
    <main className="page page--wide">
      <PageHeader
        section="Account"
        title="Settings"
        lede={
          businesses.length > 1
            ? `${businesses.length} brands on this account. The details on the left are ${business?.name ?? 'the active brand'}'s; switch brands on the right.`
            : 'The setup answers for your brand, and how you sign in. Everything else about the brand is in the brand profile.'
        }
      />

      <div className="settings-layout">
        <div className="settings-layout__main">
          {business ? (
            // Keyed on the brand, so switching brands remounts the form on the
            // new record while a save keeps the "Saved" note in place.
            <>
              <BrandSettingsForm key={business.id} business={business} />
              {/* Who works on this brand; the owner invites and removes. */}
              <TeamCard key={`team-${business.id}`} business={business} />
              {/* The daily news autopost is a brand setting too: one row per
                  brand, owner-written, read by the worker every quarter hour. */}
              <NewsAutopostCard key={`autopost-${business.id}`} business={business} />
            </>
          ) : (
            <section className="wizard settings-card">
              <p className="form__error" role="alert">
                {error ?? 'Could not load your brand.'}
              </p>
            </section>
          )}
        </div>

        <aside className="settings-layout__aside">
          <PlanCard />
          <BrandsCard />
          <PasswordSettingsForm />
          <AppearanceCard />
          {/* Renders nothing where installing is not possible or already done. */}
          <InstallCard />
          {/* This device's push notifications; hidden until the server answers. */}
          <NotificationsCard />
          <DangerZone business={business} email={user?.email ?? null} />
        </aside>
      </div>
    </main>
  );
}
